import type { Granularity } from 'src/context/api/campaignLeaks/api';
import type { CampaignLeaksT } from '../shared/i18n';

export const GRANULARITIES: Granularity[] = ['weekly', 'monthly', 'quarterly', 'yearly'];

export const granularityOptions = (ct: CampaignLeaksT) =>
  GRANULARITIES.map((value) => ({ value, label: ct(`granularities.${value}`) }));

const UNITS: Record<Granularity, { singular: string; plural: string; max: number }> = {
  weekly: { singular: 'week', plural: 'weeks', max: 12 },
  monthly: { singular: 'month', plural: 'months', max: 12 },
  quarterly: { singular: 'quarter', plural: 'quarters', max: 8 },
  yearly: { singular: 'year', plural: 'years', max: 2 },
};

export type PeriodOption = { value: string; label: string };

/** Period lists of the maquette (7846:26033): This …, Last …, Last 2 … → Last N …. */
export const periodOptions = (granularity: Granularity, ct: CampaignLeaksT): PeriodOption[] => {
  const { singular, plural, max } = UNITS[granularity];
  const options: PeriodOption[] = [
    { value: `this_${singular}`, label: ct(`periods.this_${singular}`) },
    { value: `last_${singular}`, label: ct(`periods.last_${singular}`) },
  ];
  for (let n = 2; n <= max; n += 1) {
    options.push({ value: `last_${n}_${plural}`, label: ct(`periods.last_n_${plural}`, { count: n }) });
  }
  return options;
};

export const DEFAULT_GRANULARITY: Granularity = 'weekly';
export const DEFAULT_PERIOD = 'last_12_weeks';

/** Equivalent periods across granularities (last_12_weeks → last_3_months → last_4_quarters → last_year). */
const EQUIVALENTS: Record<Granularity, string> = {
  weekly: 'last_12_weeks',
  monthly: 'last_3_months',
  quarterly: 'last_4_quarters',
  yearly: 'last_year',
};

/** Period to select after a granularity change: the equivalent one, otherwise the first "Last …" option. */
export const equivalentPeriod = (period: string, to: Granularity) => {
  const isEquivalent = Object.values(EQUIVALENTS).includes(period);
  return isEquivalent ? EQUIVALENTS[to] : `last_${UNITS[to].singular}`;
};

/** "This …" has no data yet on the first day of its unit (data stops at D-1). */
export const isThisPeriodEmpty = (granularity: Granularity, today: Date) => {
  const day = today.getDate();
  const month = today.getMonth();
  switch (granularity) {
    case 'weekly':
      return today.getDay() === 1;
    case 'monthly':
      return day === 1;
    case 'quarterly':
      return day === 1 && month % 3 === 0;
    case 'yearly':
      return day === 1 && month === 0;
    default:
      return false;
  }
};

export const isPeriodDisabled = (granularity: Granularity, period: string, today: Date) =>
  period.startsWith('this_') && isThisPeriodEmpty(granularity, today);
